import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { JobOrder } from "../../interfaces/job.interface";
import { ReactComponent as TruckOnMapIcon } from "../../assets/icons/truck-on-map.svg";
import "./MapComponent.scss";
import ContainerType from "../ContainerType/ContainerType";
import { containerSizeStringToNumber } from "../../utils/general";

interface MapComponentProps {
  job: JobOrder;
}

// Static map styles that don't need to be recreated on each render
const MAP_STYLES = [
  {
    elementType: "geometry",
    stylers: [
      {
        color: "#f5f5f5",
      },
    ],
  },
  {
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#f5f5f5",
      },
    ],
  },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#bdbdbd",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [
      {
        color: "#eeeeee",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [
      {
        color: "#e5e5e5",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [
      {
        color: "#ffffff",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [
      {
        color: "#dadada",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
  {
    featureType: "transit.line",
    elementType: "geometry",
    stylers: [
      {
        color: "#e5e5e5",
      },
    ],
  },
  {
    featureType: "transit.station",
    elementType: "geometry",
    stylers: [
      {
        color: "#eeeeee",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        color: "#c9c9c9",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
];

// Static map container styles
const MAP_CONTAINER_STYLES = {
  width: "100%",
  height: "100%",
  minHeight: "300px",
  position: "relative" as const,
  borderRadius: "10px",
  overflow: "hidden",
};

const MapComponent: React.FC<MapComponentProps> = React.memo(({ job }) => {
  // Use refs for values that don't need to trigger re-renders
  const mapRef = useRef<google.maps.Map | null>(null);
  const pathAnimationRef = useRef<google.maps.Polyline | null>(null);
  const sourceToDestPathRef = useRef<google.maps.Polyline | null>(null);
  const pathTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mapInitializedRef = useRef<boolean>(false);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(
    null
  );
  const markersRef = useRef<{
    source: {
      marker: google.maps.Marker | null;
      label: google.maps.Marker | null;
      infoWindow: google.maps.InfoWindow | null;
    };
    destination: {
      marker: google.maps.Marker | null;
      label: google.maps.Marker | null;
      infoWindow: google.maps.InfoWindow | null;
    };
  }>({
    source: { marker: null, label: null, infoWindow: null },
    destination: { marker: null, label: null, infoWindow: null },
  });
  const circlesRef = useRef<{
    source: google.maps.Circle | null;
    destination: google.maps.Circle | null;
  }>({
    source: null,
    destination: null,
  });
  const jobRef = useRef<JobOrder>(job);
  const distanceRef = useRef<string>("");
  const estimatedTimeRef = useRef<string>("");
  const showPathRef = useRef<boolean>(false);
  const [isMapCentered, setIsMapCentered] = useState(true);
  const userInteractionRef = useRef(false);

  // Update jobRef when job prop changes
  useEffect(() => {
    jobRef.current = job;
  }, [job]);

  // Memoize source and destination positions
  const { sourcePosition, destinationPosition } = useMemo(() => {
    return {
      sourcePosition: {
        lat: parseFloat(job.sourceLatitude as string),
        lng: parseFloat(job.sourceLongitude as string),
      },
      destinationPosition: {
        lat: parseFloat(job.destLatitude as string),
        lng: parseFloat(job.destLongitude as string),
      },
    };
  }, [
    job.sourceLatitude,
    job.sourceLongitude,
    job.destLatitude,
    job.destLongitude,
  ]);

  // Calculate bearing between two points - memoized with useCallback
  const calculateBearing = useCallback(
    (start: google.maps.LatLng, end: google.maps.LatLng) => {
      const startLat = (start.lat() * Math.PI) / 180;
      const startLng = (start.lng() * Math.PI) / 180;
      const endLat = (end.lat() * Math.PI) / 180;
      const endLng = (end.lng() * Math.PI) / 180;

      const y = Math.sin(endLng - startLng) * Math.cos(endLat);
      const x =
        Math.cos(startLat) * Math.sin(endLat) -
        Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);
      const bearing = (Math.atan2(y, x) * 180) / Math.PI;
      return (bearing + 360) % 360;
    },
    []
  );

  // Animate path drawing - memoized with useCallback
  const animatePath = useCallback(
    (path: google.maps.LatLng[], map: google.maps.Map) => {
      if (pathAnimationRef.current) {
        pathAnimationRef.current.setMap(null);
      }

      // Create the main polyline with initial path
      const polyline = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: "#1062b9", // Blue color
        strokeOpacity: 0.8,
        strokeWeight: 5,
        map: map,
        zIndex: 1,
      });

      // Store reference for cleanup
      pathAnimationRef.current = polyline;

      // Create shimmering effect
      let dashOffset = 0;
      const dashLength = 20; // Length of each dash
      const gapLength = 10; // Length of gap between dashes
      const totalLength = dashLength + gapLength;

      const animateShimmer = () => {
        dashOffset = (dashOffset + 1) % totalLength;

        // Create the dash pattern
        const dashPattern = `${dashLength} ${gapLength}`;
        const offset = `${dashOffset} ${totalLength - dashOffset}`;

        polyline.setOptions({
          strokeOpacity: 0.8,
          strokeWeight: 5,
          icons: [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 1,
                strokeWeight: 5,
                scale: 1,
              },
              offset: `${dashOffset}px`,
              repeat: `${totalLength}px`,
            },
          ],
        });

        requestAnimationFrame(animateShimmer);
      };

      // Start the shimmer animation
      animateShimmer();
    },
    []
  );

  // Function to show source to destination path - memoized with useCallback
  const showSourceToDestinationPath = useCallback(() => {
    if (!mapRef.current || !jobRef.current) return;

    // Clear any existing paths
    if (sourceToDestPathRef.current) {
      sourceToDestPathRef.current.setMap(null);
    }

    // Clear any existing timeout
    if (pathTimeoutRef.current) {
      clearTimeout(pathTimeoutRef.current);
    }

    // Hide the directions renderer if it exists
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }

    // Create the source to destination path (black)
    sourceToDestPathRef.current = new google.maps.Polyline({
      path: [
        new google.maps.LatLng(sourcePosition.lat, sourcePosition.lng),
        new google.maps.LatLng(
          destinationPosition.lat,
          destinationPosition.lng
        ),
      ],
      geodesic: true,
      strokeColor: "#000000",
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: mapRef.current,
    });

    // Create shimmering effect for the black path
    let dashOffset = 0;
    const dashLength = 20;
    const gapLength = 10;
    const totalLength = dashLength + gapLength;

    const animateShimmer = () => {
      dashOffset = (dashOffset + 1) % totalLength;

      sourceToDestPathRef.current?.setOptions({
        strokeOpacity: 0.8,
        strokeWeight: 3,
        icons: [
          {
            icon: {
              path: "M 0,-1 0,1",
              strokeOpacity: 1,
              strokeWeight: 3,
              scale: 1,
            },
            offset: `${dashOffset}px`,
            repeat: `${totalLength}px`,
          },
        ],
      });

      requestAnimationFrame(animateShimmer);
    };

    // Start the shimmer animation
    animateShimmer();

    // Fit bounds to show the source and destination
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(
      new google.maps.LatLng(sourcePosition.lat, sourcePosition.lng)
    );
    bounds.extend(
      new google.maps.LatLng(destinationPosition.lat, destinationPosition.lng)
    );

    // Add padding to the bounds
    mapRef.current.fitBounds(bounds);

    // Add a small delay to ensure the map is properly centered
    setTimeout(() => {
      // Zoom out slightly to add padding
      const currentZoom = mapRef.current?.getZoom();
      if (currentZoom) {
        mapRef.current?.setZoom(currentZoom - 1);
      }
    }, 100);

    showPathRef.current = true;

    // Set timeout to hide path
    pathTimeoutRef.current = setTimeout(() => {
      if (sourceToDestPathRef.current) {
        sourceToDestPathRef.current.setMap(null);
      }
      showPathRef.current = false;

      // Restore the directions renderer
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(mapRef.current);
      }
    }, 10000); // Show path for 10 seconds
  }, [sourcePosition, destinationPosition]);

  // Function to center map on source and destination
  const centerMap = useCallback(() => {
    if (!mapRef.current) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(
      new google.maps.LatLng(sourcePosition.lat, sourcePosition.lng)
    );
    bounds.extend(
      new google.maps.LatLng(destinationPosition.lat, destinationPosition.lng)
    );

    // Add padding to the bounds
    mapRef.current.fitBounds(bounds);

    // Add a small delay to ensure the map is properly centered
    setTimeout(() => {
      // Zoom out slightly to add padding
      const currentZoom = mapRef.current?.getZoom();
      if (currentZoom) {
        mapRef.current?.setZoom(currentZoom - 1);
      }
    }, 100);

    setIsMapCentered(true);
    userInteractionRef.current = false;
  }, [sourcePosition, destinationPosition]);

  // Memoize map options
  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      streetViewControl: false,
      fullscreenControl: true,
      zoom: 8,
      center: sourcePosition,
      styles: MAP_STYLES,
    }),
    [sourcePosition]
  );

  // Add this before the return statement
  type ContainerStatus = {
    INITIATED: number;
    ASSIGNED: number;
    IN_TRANSIT: number;
    COMPLETED: number;
    DROPPED: number;
  };

  type ContainerStat = {
    size: string;
    type: string;
    count: number;
    status: ContainerStatus;
  };

  const getContainerStats = useCallback((breakups: JobOrder["jobBreakups"]) => {
    return breakups.reduce((acc, breakup) => {
      const key = `${breakup.containerSize}-${breakup.containerType}`;
      if (!acc[key]) {
        acc[key] = {
          size: breakup.containerSize,
          type: breakup.containerType,
          count: 0,
          status: {
            INITIATED: 0,
            ASSIGNED: 0,
            IN_TRANSIT: 0,
            COMPLETED: 0,
            DROPPED: 0,
          },
        };
      }
      acc[key].count += breakup.count;

      breakup.jobBreakUpDetails?.forEach((detail) => {
        const tripStatus = detail.tripStatus as keyof ContainerStatus;
        if (tripStatus && acc[key].status.hasOwnProperty(tripStatus)) {
          acc[key].status[tripStatus]++;
        }
      });

      return acc;
    }, {} as Record<string, ContainerStat>);
  }, []);

  // Update the createMarkerWithLabel function
  const createMarkerWithLabel = useCallback(
    (
      position: google.maps.LatLngLiteral,
      title: string,
      label: string,
      imageUrl?: string,
      iconColor?: string,
      isSource: boolean = true
    ) => {
      if (!mapRef.current)
        return { marker: null, labelMarker: null, infoWindow: null };

      // Create marker
      const marker = new google.maps.Marker({
        position,
        map: mapRef.current,
        title,
        icon: {
          path: "M10.5 0.368164C4.97662 0.368164 0.5 4.84586 0.5 10.3682C0.5 13.4471 1.89154 16.1999 4.07974 18.0348L10.5 24.4562L16.9203 18.0359C19.1085 16.2011 20.5 13.4482 20.5 10.3692C20.5 4.84586 16.0231 0.368164 10.5 0.368164ZM10.5 17.0312C6.99332 17.0312 4.15067 14.1885 4.15067 10.6818C4.15067 7.17624 6.99332 4.33359 10.5 4.33359C14.0067 4.33359 16.8493 7.17624 16.8493 10.6829C16.8493 14.1885 14.0067 17.0312 10.5 17.0312Z",
          fillColor: iconColor,
          fillOpacity: 1.0,
          strokeColor: "#000000",
          strokeWeight: 1,
          scale: 1,
          anchor: new google.maps.Point(12, 24),
        },
      });

      // Create label with background
      const labelMarker = new google.maps.Marker({
        position: {
          lat: position.lat + 0.005,
          lng: position.lng,
        },
        map: mapRef.current,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 0,
        },
        label: {
          text: label,
          color: "#37372d",
          fontWeight: "bold",
          fontSize: "14px",
          className: "map-label",
        },
      });

      // Get container stats
      const containerStats = getContainerStats(jobRef.current.jobBreakups);
      const containerStatsHtml = Object.entries(containerStats)
        .map(([_, stat]: [string, ContainerStat]) => {
          const sizeNumber = containerSizeStringToNumber(stat.size) ?? 0;

          // Get container numbers for each status
          const containerNumbers = jobRef.current.jobBreakups
            .filter(
              (breakup) =>
                breakup.containerSize === stat.size &&
                breakup.containerType === stat.type
            )
            .flatMap((breakup) =>
              breakup.jobBreakUpDetails
                .filter((detail) =>
                  isSource
                    ? detail.tripStatus === "INITIATED"
                    : detail.tripStatus === "DROPPED"
                )
                .map((detail) => detail.containerNumber)
                .filter(Boolean)
            );

          // Filter statuses based on location
          const relevantStatuses = isSource
            ? { INITIATED: stat.status.INITIATED } // Show pending pickups at source
            : { DROPPED: stat.status.DROPPED }; // Show delivered containers at destination

          return `
              <div class="container-stat" style="
                background-color: #f8f9fa;
                border-radius: 6px;
                padding: 12px;
              ">
                <div class="container-header" style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                ">
                  <div class="container-type-wrapper">
                    <span class="container-type" style="
                      border: 1px solid ${
                        sizeNumber === 20
                          ? "#1D3557"
                          : sizeNumber === 40
                          ? "#1062b9"
                          : "#69B9BC"
                      };
                      background-color: ${
                        sizeNumber === 20
                          ? "rgba(29, 53, 87, 0.10)"
                          : sizeNumber === 40
                          ? "rgba(69, 123, 157, 0.10)"
                          : "rgba(105, 185, 188, 0.10)"
                      };
                      color: ${
                        sizeNumber === 20
                          ? "#1D3557"
                          : sizeNumber === 40
                          ? "#1062b9"
                          : "#69B9BC"
                      };
                      padding: 4px 12px;
                      border-radius: 4px;
                      font-weight: 600;
                      font-size: 13px;
                      display: inline-flex;
                      align-items: center;
                      gap: 6px;
                      width: 50px;
                      justify-content: center;
                    ">
                      ${sizeNumber} ${stat.type}
                    </span>
                  </div>
                  <span class="container-count" style="
                    font-size: 13px;
                    font-weight: 500;
                    color: #495057;
                  ">${stat.count} containers</span>
                </div>
                <div class="container-status" style="
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                ">
                  ${Object.entries(relevantStatuses)
                    .filter(([_, count]) => count > 0)
                    .map(([status, count]) => {
                      const statusStyles = {
                        INITIATED: {
                          backgroundColor: "#FFF3E0",
                          color: "#E65100",
                          borderColor: "#FFB74D",
                          label: "Pending Pickup",
                          icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 8V12L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                          </svg>`,
                        },
                        DROPPED: {
                          backgroundColor: "#E8F5E9",
                          color: "#1B5E20",
                          borderColor: "#81C784",
                          label: "Delivered",
                          icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>`,
                        },
                      };
                      const style =
                        statusStyles[status as keyof typeof statusStyles];
                      return `
                        <div style="
                          display: flex;
                          flex-direction: column;
                          gap: 8px;
                        ">
                          <div style="
                            display: flex;
                            align-items: center;
                            gap: 8px;
                          ">
                            <span class="status-badge" style="
                              background-color: ${style.backgroundColor};
                              color: ${style.color};
                              border: 1px solid ${style.borderColor};
                              padding: 4px 12px;
                              border-radius: 16px;
                              font-size: 12px;
                              font-weight: 500;
                              white-space: nowrap;
                              display: inline-flex;
                              align-items: center;
                              gap: 6px;
                              width: fit-content;
                            ">
                              ${style.icon}
                              ${style.label}
                            </span>
                            <span style="
                              font-size: 12px;
                              color: #6c757d;
                              font-weight: 500;
                            ">${count} containers</span>
                          </div>
                          ${
                            containerNumbers.length > 0
                              ? `
                            <div style="
                              display: grid;
                              grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
                              gap: 6px;
                              margin-left: 4px;
                            ">
                              ${containerNumbers
                                .map(
                                  (number) => `
                                <div style="
                                  background-color: #fff;
                                  border: 1px solid #dee2e6;
                                  border-radius: 4px;
                                  padding: 6px 8px;
                                  display: flex;
                                  align-items: center;
                                  gap: 6px;
                                ">
                                 
                                  <span style="
                                    font-size: 11px;
                                    color: #495057;
                                    font-family: 'SF Mono', 'Roboto Mono', monospace;
                                    white-space: nowrap;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                  ">${number}</span>
                                </div>
                              `
                                )
                                .join("")}
                            </div>
                          `
                              : ""
                          }
                        </div>
                      `;
                    })
                    .join("")}
                </div>
              </div>
            `;
        })
        .join("");

      // Calculate job progress
      const totalContainers = jobRef.current.jobBreakups.reduce(
        (total, breakup) => total + breakup.count,
        0
      );
      const deliveredContainers = jobRef.current.jobBreakups.reduce(
        (total, breakup) =>
          total +
          breakup.jobBreakUpDetails.filter(
            (detail) => detail.tripStatus === "DROPPED"
          ).length,
        0
      );
      const progressPercentage = Math.round(
        (deliveredContainers / totalContainers) * 100
      );

      // Format dates
      const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      };

      // Create info window with container stats and job progress
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div class="custom-info-window" style="
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            min-width: 280px;
            max-width: min(320px, calc(100vw - 32px));
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            overflow: hidden;
          ">
            <div class="header" style="
              padding: 16px;
              background-color: #f8f9fa;
              border-bottom: 1px solid #e9ecef;
            ">
              <div style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 12px;
                flex-wrap: wrap;
              ">
                <div style="flex: 1; min-width: 200px;">
                  <div style="
                    font-size: 11px;
                    color: #6c757d;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                  ">${isSource ? "Source" : "Destination"}</div>
                  <h3 style="
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #212529;
                    line-height: 1.4;
                    word-break: break-word;
                  ">${title}</h3>
                </div>
                <div style="
                  background-color: ${
                    jobRef.current.jobType === "PICKUP" ? "#E3F2FD" : "#F3E5F5"
                  };
                  color: ${
                    jobRef.current.jobType === "PICKUP" ? "#1062b9" : "#7B1FA2"
                  };
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 12px;
                  font-weight: 500;
                  white-space: nowrap;
                  height: fit-content;
                ">${jobRef.current.jobType}</div>
              </div>
              <div style="
                margin-top: 12px;
                font-size: 12px;
                color: #495057;
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
              ">
                <div style="
                  display: flex;
                  gap: 16px;
                  flex-wrap: wrap;
                ">
                  <div>
                    <span style="color: #868e96;">Vessel:</span>
                    <span style="margin-left: 4px; font-weight: 500;">${
                      jobRef.current.vesselName
                    }</span>
                  </div>
                  <div>
                    <span style="color: #868e96;">Voyage:</span>
                    <span style="margin-left: 4px; font-weight: 500;">${
                      jobRef.current.voyageVia
                    }</span>
                  </div>
                </div>
                <div style="
                  display: flex;
                  flex-wrap: wrap;
                  gap: 4px;
                ">
                  <span style="color: #868e96;">Gate Dates:</span>
                  <span style="margin-left: 4px; font-weight: 500;">
                    ${formatDate(jobRef.current.gateOpenDate)} - ${formatDate(
          jobRef.current.gateCloseDate
        )}
                  </span>
                </div>
              </div>
            </div>
            <div class="job-progress" style="
              padding: 16px;
              background-color: #fff;
              border-bottom: 1px solid #e9ecef;
            ">
              <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
                flex-wrap: wrap;
                gap: 8px;
              ">
                <span style="
                  color: #495057;
                  font-size: 13px;
                  font-weight: 500;
                ">Job Progress</span>
                <span style="
                  color: #01BA65;
                  font-size: 13px;
                  font-weight: 600;
                ">${progressPercentage}%</span>
              </div>
              <div style="
                width: 100%;
                height: 6px;
                background-color: #e9ecef;
                border-radius: 3px;
                overflow: hidden;
              ">
                <div style="
                  width: ${progressPercentage}%;
                  height: 100%;
                  background-color: #01BA65;
                  border-radius: 3px;
                  transition: width 0.3s ease;
                "></div>
              </div>
              <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-top: 8px;
                font-size: 12px;
                color: #6c757d;
                flex-wrap: wrap;
                gap: 8px;
              ">
                <span>${deliveredContainers} delivered</span>
                <span>${totalContainers} total</span>
              </div>
            </div>
            <div class="content" style="
              padding: 16px;
              background-color: #fff;
            ">
              <div class="container-stats-section" style="
                display: flex;
                flex-direction: column;
                gap: 12px;
              ">
                ${containerStatsHtml}
              </div>
            </div>
          </div>
        `,
        maxWidth: 320,
        pixelOffset: new google.maps.Size(0, -10),
      });

      marker.addListener("click", () => {
        infoWindow.open(mapRef.current, marker);
      });

      return { marker, labelMarker, infoWindow };
    },
    [mapRef, getContainerStats]
  );

  // Memoize the createButton function
  const createButton = useCallback(
    (text: string, icon: string, onClick: () => void) => {
      const button = document.createElement("button");
      button.className = "map-control-button";
      button.style.padding = "8px 12px";
      button.style.backgroundColor = "white";
      button.style.border = "1px solid #ccc";
      button.style.borderRadius = "4px";
      button.style.cursor = "pointer";
      button.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
      button.style.fontSize = "14px";
      button.style.fontWeight = "500";
      button.style.color = "#333";
      button.style.transition = "all 0.2s ease";
      button.style.display = "flex";
      button.style.alignItems = "center";
      button.style.gap = "8px";

      // Add icon
      const iconSpan = document.createElement("span");
      iconSpan.innerHTML = icon;
      iconSpan.style.display = "flex";
      iconSpan.style.alignItems = "center";
      button.appendChild(iconSpan);

      // Add text
      const textSpan = document.createElement("span");
      textSpan.textContent = text;
      button.appendChild(textSpan);

      button.onmouseover = () => {
        button.style.backgroundColor = "#f1f1f1";
      };

      button.onmouseout = () => {
        button.style.backgroundColor = "white";
      };

      button.onclick = onClick;
      return button;
    },
    []
  );

  // Function to clear existing markers and circles
  const clearMapElements = useCallback(() => {
    // Clear markers and info windows
    Object.values(markersRef.current).forEach(
      ({ marker, label, infoWindow }) => {
        if (marker) marker.setMap(null);
        if (label) label.setMap(null);
        if (infoWindow) infoWindow.close();
      }
    );

    // Clear circles
    Object.values(circlesRef.current).forEach((circle) => {
      if (circle) circle.setMap(null);
    });

    // Clear paths
    if (pathAnimationRef.current) {
      pathAnimationRef.current.setMap(null);
    }
    if (sourceToDestPathRef.current) {
      sourceToDestPathRef.current.setMap(null);
    }
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }
  }, []);

  // Function to update map elements
  const updateMapElements = useCallback(() => {
    if (!mapRef.current) return;

    // Clear existing elements
    clearMapElements();

    // Create new markers
    const sourceMarker = createMarkerWithLabel(
      sourcePosition,
      job.source,
      job.source,
      "",
      "#cbd2d9",
      true
    );
    markersRef.current.source = {
      marker: sourceMarker.marker,
      label: sourceMarker.labelMarker,
      infoWindow: sourceMarker.infoWindow,
    };

    const destMarker = createMarkerWithLabel(
      destinationPosition,
      job.destination,
      job.destination,
      "",
      "#1062b9",
      false
    );
    markersRef.current.destination = {
      marker: destMarker.marker,
      label: destMarker.labelMarker,
      infoWindow: destMarker.infoWindow,
    };

    // Create new circles
    circlesRef.current.source = new google.maps.Circle({
      strokeColor: "#cbd2d9",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#cbd2d9",
      fillOpacity: 0.15,
      map: mapRef.current,
      center: sourcePosition,
      radius: 300,
    });

    circlesRef.current.destination = new google.maps.Circle({
      strokeColor: "#1062b9",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#1062b9",
      fillOpacity: 0.15,
      map: mapRef.current,
      center: destinationPosition,
      radius: 300,
    });

    // Update directions
    const directionsService = new google.maps.DirectionsService();
    const directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: { strokeColor: "#000", strokeWeight: 4 },
    });
    directionsRenderer.setMap(mapRef.current);
    directionsRendererRef.current = directionsRenderer;

    directionsService.route(
      {
        origin: sourcePosition,
        destination: destinationPosition,
        travelMode: google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(Date.now()),
          trafficModel: google.maps.TrafficModel.BEST_GUESS,
        },
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          directionsRenderer.setDirections(result);

          const leg = result.routes[0].legs[0];
          if (leg) {
            distanceRef.current = leg.distance?.text || "N/A";
            estimatedTimeRef.current = leg.duration?.text || "N/A";

            // Animate the path
            const path = result.routes[0].overview_path;
            if (path) {
              setTimeout(() => {
                animatePath(path, mapRef.current!);
              }, 500);
            }
          }
        } else {
          console.error(`Error fetching directions: ${status}`, result);
        }
      }
    );

    // Center map
    centerMap();
  }, [
    sourcePosition,
    destinationPosition,
    job.source,
    job.destination,
    createMarkerWithLabel,
    clearMapElements,
    centerMap,
    animatePath,
  ]);

  // Initialize map
  useEffect(() => {
    if (mapInitializedRef.current) return;
    mapInitializedRef.current = true;

    if (window.google) {
      const mapInstance = new google.maps.Map(
        document.getElementById("mapComponent") as HTMLElement,
        mapOptions
      );
      mapRef.current = mapInstance;

      // Add traffic layer
      const trafficLayer = new google.maps.TrafficLayer();
      google.maps.event.addListenerOnce(mapInstance, "idle", () => {
        trafficLayer.setMap(mapInstance);
      });

      // Add user interaction listeners
      mapInstance.addListener("dragend", () => {
        userInteractionRef.current = true;
        setIsMapCentered(false);
      });

      mapInstance.addListener("zoom_changed", () => {
        userInteractionRef.current = true;
        setIsMapCentered(false);
      });

      // Add center map button
      const centerMapButton = createButton(
        "Center Map",
        `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" 
            fill="currentColor"/>
        </svg>`,
        () => {
          centerMap();
        }
      );

      // Add margin to the button
      centerMapButton.style.margin = "16px";

      // Add the button to the map
      mapInstance.controls[google.maps.ControlPosition.BOTTOM_RIGHT].push(
        centerMapButton
      );

      // Initial map elements setup
      updateMapElements();

      // Cleanup
      return () => {
        clearMapElements();
        mapInstance.controls[google.maps.ControlPosition.BOTTOM_RIGHT].clear();
        mapInitializedRef.current = false;
      };
    }
  }, [mapOptions, updateMapElements, clearMapElements, centerMap]);

  // Update map when job changes
  useEffect(() => {
    if (mapRef.current && mapInitializedRef.current) {
      updateMapElements();
    }
  }, [job, updateMapElements]);

  // Add this before the return statement
  const containerStats = useMemo(() => {
    type ContainerStatus = {
      INITIATED: number;
      ASSIGNED: number;
      IN_TRANSIT: number;
      COMPLETED: number;
      DROPPED: number;
    };

    type ContainerStat = {
      size: string;
      type: string;
      count: number;
      status: ContainerStatus;
    };

    const stats = job.jobBreakups.reduce((acc, breakup) => {
      const key = `${breakup.containerSize}-${breakup.containerType}`;
      if (!acc[key]) {
        acc[key] = {
          size: breakup.containerSize,
          type: breakup.containerType,
          count: 0,
          status: {
            INITIATED: 0,
            ASSIGNED: 0,
            IN_TRANSIT: 0,
            COMPLETED: 0,
            DROPPED: 0,
          },
        };
      }
      acc[key].count += breakup.count;

      // Count statuses from jobBreakUpDetails
      breakup.jobBreakUpDetails?.forEach((detail) => {
        const tripStatus = detail.tripStatus as keyof ContainerStatus;
        if (tripStatus && acc[key].status.hasOwnProperty(tripStatus)) {
          acc[key].status[tripStatus]++;
        }
      });

      return acc;
    }, {} as Record<string, ContainerStat>);

    return Object.values(stats);
  }, [job.jobBreakups]);

  return (
    <div style={MAP_CONTAINER_STYLES}>
      <div id="mapComponent" style={MAP_CONTAINER_STYLES} />
    </div>
  );
});

// Add display name for debugging
MapComponent.displayName = "MapComponent";

export default MapComponent;
